import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/collapsible.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  PointInsightUserMeReadModel,
  ValuePerMonthInsightUserMeReadModel
} from '@r/api/generated/client.schemas.ts';
import pointsTranslation from '@r/translations/points-translation.ts';

interface Props {
  pointsHistory: PointInsightUserMeReadModel[];
  totalPointsPerMonth: ValuePerMonthInsightUserMeReadModel[];
}

function formatMonthLabel(month: number, year: number): string {
  const name = format(new Date(year, month - 1), 'MMMM', { locale: fr });
  return `${name.charAt(0).toUpperCase() + name.slice(1)} ${year}`;
}

function formatItemDate(isoDate: string): string {
  return format(new Date(isoDate), 'dd/MM/yyyy', { locale: fr });
}

export function PointsHistoryAccordion({ pointsHistory, totalPointsPerMonth }: Props) {
  const { t } = useTranslation();

  const monthTotalMap = new Map(totalPointsPerMonth.map((m) => [`${m.year}-${m.month}`, m.value]));

  // Group points history by month
  const groupedMonths = [
    ...pointsHistory
      .reduce((acc, p) => {
        const d = new Date(p.valueDate);
        const month = d.getMonth() + 1;
        const year = d.getFullYear();
        const key = `${year}-${month}`;
        if (!acc.has(key)) acc.set(key, { month, year, items: [] });
        acc.get(key)?.items.push(p);
        return acc;
      }, new Map<string, { month: number; year: number; items: PointInsightUserMeReadModel[] }>())
      .values()
  ].sort((a, b) => (a.year !== b.year ? b.year - a.year : b.month - a.month));

  const firstKey =
    groupedMonths.length > 0 ? `${groupedMonths[0].year}-${groupedMonths[0].month}` : null;

  const [openMonths, setOpenMonths] = useState<Set<string>>(
    firstKey ? new Set([firstKey]) : new Set()
  );

  const toggle = (key: string) =>
    setOpenMonths((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col gap-1">
      {groupedMonths.map(({ month, year, items }) => {
        const key = `${year}-${month}`;
        const value = monthTotalMap.get(key) ?? items.reduce((sum, p) => sum + p.amount, 0);
        const sortedItems = [...items].sort(
          (a, b) => new Date(b.valueDate).getTime() - new Date(a.valueDate).getTime()
        );

        return (
          <Collapsible key={key} open={openMonths.has(key)} onOpenChange={() => toggle(key)}>
            <CollapsibleTrigger className="flex gap-3 items-center px-3 py-3 w-full rounded-lg transition-colors group bg-muted hover:bg-muted/80">
              <span className="flex justify-center items-center w-6 h-6 rounded bg-primary text-primary-foreground shrink-0">
                <Plus className="w-3.5 h-3.5 group-data-[state=open]:hidden" />
                <Minus className="w-3.5 h-3.5 hidden group-data-[state=open]:block" />
              </span>
              <span className="flex-1 text-sm font-bold text-left">
                {formatMonthLabel(month, year)}
              </span>
              <span className="text-sm font-medium text-primary shrink-0">
                {value > 0 ? '+' : ''}
                {pointsTranslation(t, value, true)}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {sortedItems.map((item, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-1 items-start px-3 py-2 text-sm md:flex-row md:items-center md:gap-4"
                >
                  <span className="text-muted-foreground shrink-0">
                    {formatItemDate(item.valueDate)}
                  </span>
                  {item.label && <span className="flex-1">{item.label}</span>}
                  <span
                    className={
                      item.amount > 0
                        ? 'text-primary shrink-0 ml-auto'
                        : item.amount < 0
                          ? 'text-destructive shrink-0 ml-auto'
                          : 'text-muted-foreground shrink-0 ml-auto'
                    }
                  >
                    {item.amount > 0 ? '+' : ''}
                    {pointsTranslation(t, item.amount, true)}
                  </span>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
